import { LINKEDIN, META } from "./adsPlatformIds.js";
import { pickLeadFormPreviewStorageFields } from "./leadFormPreview.js";
import {
  DEFAULT_LINKEDIN_LEAD_FORM_CALL_TO_ACTION,
  DEFAULT_LINKEDIN_LEAD_FORM_PROFILE_FIELDS,
  LINKEDIN_LEAD_FORM_CALL_TO_ACTION_OPTIONS,
  LINKEDIN_LEAD_FORM_FIELD_LIMITS,
  MAX_LINKEDIN_LEAD_FORM_QUESTIONS,
} from "./linkedInLeadForm.js";
import { LINKEDIN_LEAD_FORM_PROFILE_FIELD_OPTIONS } from "./linkedInLeadFormProfileFields.js";
import {
  DEFAULT_META_INSTANT_FORM_CONTACT_FIELDS,
  MAX_META_INSTANT_FORM_QUESTIONS,
  MAX_MULTIPLE_CHOICE_OPTIONS,
  META_INSTANT_FORM_ACTION_BOOK_TIME,
  META_INSTANT_FORM_ACTION_VIEW_WEBSITE,
  META_INSTANT_FORM_CONTACT_FIELD_OPTIONS,
  META_INSTANT_FORM_FIELD_LIMITS,
  META_INSTANT_FORM_TYPE_HIGHER_INTENT,
  META_INSTANT_FORM_TYPE_MORE_VOLUME,
  MIN_MULTIPLE_CHOICE_OPTIONS,
  resolveMetaInstantFormLocale,
} from "./metaInstantForm.js";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "./regex.js";

const OPEN_DRAFT_STATUS = "draft";
const EMPTY_CUSTOM_QUESTION_OPTIONS = ["", ""];
const SAFE_ERROR_NAMES = new Set([
  "Error",
  "TypeError",
  "RangeError",
  "ReferenceError",
]);
const META_CONTACT_FIELD_VALUES = new Set(
  META_INSTANT_FORM_CONTACT_FIELD_OPTIONS.map((option) => option.value)
);
const LINKEDIN_PROFILE_FIELD_VALUES = new Set(
  LINKEDIN_LEAD_FORM_PROFILE_FIELD_OPTIONS.map((option) => option.value)
);
const LINKEDIN_CALL_TO_ACTIONS = new Set(
  LINKEDIN_LEAD_FORM_CALL_TO_ACTION_OPTIONS.map((option) => option.value)
);
const META_FORM_TYPES = new Set([
  META_INSTANT_FORM_TYPE_MORE_VOLUME,
  META_INSTANT_FORM_TYPE_HIGHER_INTENT,
]);
const META_THANK_YOU_ACTIONS = new Set([
  META_INSTANT_FORM_ACTION_VIEW_WEBSITE,
  META_INSTANT_FORM_ACTION_BOOK_TIME,
]);

const EMPTY_PREPARED_LEAD_FORM = {
  initialValues: null,
  adIDs: null,
  source: null,
};

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

const truncateText = (value, max) => {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, max).trimEnd();
};

// URLs are kept whole or dropped. Slicing a URL would leave an invalid link.
const keepUrl = (value, max) => {
  if (typeof value !== "string") return "";
  const text = value.trim();
  if (!text || text.length > max) return "";
  return REGEX_URL_WITH_PROTOCOL_REQUIRED.test(text) ? text : "";
};

const customQuestionsHaveText = (customQuestions) => {
  if (!Array.isArray(customQuestions)) return false;
  return customQuestions.some((item) => {
    if (hasText(item?.question)) return true;
    if (!Array.isArray(item?.options)) return false;
    return item.options.some((option) => hasText(option));
  });
};

const isValidLeadForm = (leadForm, platform) => {
  if (!leadForm || typeof leadForm !== "object" || Array.isArray(leadForm)) {
    return false;
  }
  if (platform === LINKEDIN) {
    return (
      hasText(leadForm.name) ||
      hasText(leadForm.headline) ||
      hasText(leadForm.thankYouMessage)
    );
  }
  return (
    hasText(leadForm.name) ||
    hasText(leadForm.introHeadline) ||
    hasText(leadForm.thankYouTitle) ||
    customQuestionsHaveText(leadForm.customQuestions)
  );
};

// Backend usability rule (metaLeadFormMapper / linkedInLeadFormMapper): a
// stored form the backend would regenerate must not prefill the editor.
const isUsableLeadForm = (leadForm, platform) => {
  if (!isValidLeadForm(leadForm, platform)) return false;
  if (platform === LINKEDIN) {
    return hasText(leadForm.name) && hasText(leadForm.headline);
  }
  return hasText(leadForm.name) && hasText(leadForm.introHeadline);
};

const entityKeyOf = (entityID) =>
  entityID == null || entityID === "" ? "" : String(entityID);

const entriesOf = (value) => {
  if (Array.isArray(value)) return value.map((item, index) => [index, item]);
  if (value && typeof value === "object") return Object.entries(value);
  return [];
};

const explicitAdIDs = (group) => {
  if (!Array.isArray(group?.adIDs) || group.adIDs.length === 0) return [];
  return group.adIDs
    .filter((id) => id != null && String(id).trim() !== "")
    .map((id) => String(id));
};

// Backend writes { adIDs: [], leadForm } or key "default" when one form
// applies to every ad.
const isAllAdsGroup = (group) =>
  group?.key === "default" ||
  (Array.isArray(group?.adIDs) && group.adIDs.length === 0);

const groupsOf = (recommendations) =>
  Array.isArray(recommendations?.leadFormGroups)
    ? recommendations.leadFormGroups
    : [];

const siblingIDsForEntity = ({ adsGroups, entityID }) => {
  const entityKey = entityKeyOf(entityID);
  if (!entityKey) return [];
  for (const [, adGroup] of entriesOf(adsGroups)) {
    const ids = [];
    let found = false;
    for (const [, ad] of entriesOf(adGroup?.ads)) {
      const id = entityKeyOf(ad?.id);
      if (!id) continue;
      ids.push(id);
      if (id === entityKey) found = true;
    }
    if (found) return ids.filter((id) => id !== entityKey);
  }
  return [];
};

const resolvedGroup = ({ group, source }) => ({
  recommendation: group.leadForm,
  adIDs: explicitAdIDs(group),
  source,
});

// One decision for the form and for who receives it on save.
// Order: own group, Meta sibling in the same ad set, legacy, all-ads or
// "default" group. usableOnly follows the backend hasUsableLeadFormForAd
// (stricter form check, no other ad's group), so a miss there means the
// editor asks the backend. Display keeps the loose check and, last, the
// first valid group.
export const resolveRecommendedLeadFormForEntity = ({
  platform,
  recommendations,
  entityID,
  adsGroups,
  usableOnly = false,
} = {}) => {
  const groups = groupsOf(recommendations);
  const entityKey = entityKeyOf(entityID);
  const isAccepted = usableOnly ? isUsableLeadForm : isValidLeadForm;

  if (entityKey) {
    const ownGroup = groups.find(
      (group) =>
        explicitAdIDs(group).includes(entityKey) &&
        isAccepted(group?.leadForm, platform)
    );
    if (ownGroup) return resolvedGroup({ group: ownGroup, source: "group" });
  }

  if (platform === META && entityKey) {
    const siblingIDs = new Set(siblingIDsForEntity({ adsGroups, entityID }));
    if (siblingIDs.size > 0) {
      const siblingGroup = groups.find((group) => {
        if (!isAccepted(group?.leadForm, platform)) return false;
        return explicitAdIDs(group).some((id) => siblingIDs.has(id));
      });
      if (siblingGroup) {
        return resolvedGroup({ group: siblingGroup, source: "siblingGroup" });
      }
    }
  }

  if (isAccepted(recommendations?.leadForm, platform)) {
    return {
      recommendation: recommendations.leadForm,
      adIDs: null,
      source: "legacy",
    };
  }

  const allAdsGroup = groups.find(
    (group) => isAllAdsGroup(group) && isAccepted(group?.leadForm, platform)
  );
  if (allAdsGroup) {
    const adIDs = explicitAdIDs(allAdsGroup);
    return {
      recommendation: allAdsGroup.leadForm,
      adIDs: adIDs.length > 0 ? adIDs : null,
      source: "all",
    };
  }

  if (usableOnly) return null;

  const firstGroup = groups.find((group) =>
    isValidLeadForm(group?.leadForm, platform)
  );
  if (firstGroup) {
    return resolvedGroup({ group: firstGroup, source: "firstGroup" });
  }

  return null;
};

// The summary card reads this during render. A throwing recommendation must
// not crash the editor; Crear reports that failure from prepare.
export const readRecommendedLeadForm = (args) => {
  try {
    return resolveRecommendedLeadFormForEntity(args)?.recommendation ?? null;
  } catch {
    return null;
  }
};

const uniqueAllowedValues = ({ values, allowed, max }) => {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const next = [];
  for (const value of values) {
    if (typeof value !== "string" || !allowed.has(value) || seen.has(value)) {
      continue;
    }
    seen.add(value);
    next.push(value);
    if (next.length >= max) break;
  }
  return next;
};

const sanitizeCustomQuestions = ({ customQuestions, maxCount }) => {
  if (!Array.isArray(customQuestions) || maxCount <= 0) return [];
  const questionMax = META_INSTANT_FORM_FIELD_LIMITS.question;
  const next = [];
  for (const item of customQuestions) {
    if (next.length >= maxCount) break;
    if (!item || typeof item !== "object") continue;
    const question = truncateText(item.question, questionMax);
    if (!question) continue;
    let type = item.type === "MULTIPLE_CHOICE" ? "MULTIPLE_CHOICE" : "TEXT";
    let options = EMPTY_CUSTOM_QUESTION_OPTIONS;
    if (type === "MULTIPLE_CHOICE") {
      const parsed = [];
      const rawOptions = Array.isArray(item.options) ? item.options : [];
      for (const option of rawOptions) {
        if (typeof option !== "string") continue;
        const text = option.trim();
        if (!text) continue;
        parsed.push(text);
        if (parsed.length >= MAX_MULTIPLE_CHOICE_OPTIONS) break;
      }
      if (parsed.length < MIN_MULTIPLE_CHOICE_OPTIONS) {
        type = "TEXT";
      } else {
        options = parsed;
      }
    }
    next.push({ question, type, options });
  }
  return next;
};

const oneOf = (value, allowed, fallback) =>
  allowed.has(value) ? value : fallback;

export const buildMetaLeadFormInitialValues = ({
  recommendation,
  language,
} = {}) => {
  if (!isValidLeadForm(recommendation, META)) return null;

  const limits = META_INSTANT_FORM_FIELD_LIMITS;
  const contactFields = uniqueAllowedValues({
    values: recommendation.contactFields,
    allowed: META_CONTACT_FIELD_VALUES,
    max: MAX_META_INSTANT_FORM_QUESTIONS,
  });
  const resolvedContactFields =
    contactFields.length > 0
      ? contactFields
      : [...DEFAULT_META_INSTANT_FORM_CONTACT_FIELDS];
  const customQuestions = sanitizeCustomQuestions({
    customQuestions: recommendation.customQuestions,
    maxCount: Math.max(
      0,
      MAX_META_INSTANT_FORM_QUESTIONS - resolvedContactFields.length
    ),
  });

  return {
    name: truncateText(recommendation.name, limits.name),
    locale: resolveMetaInstantFormLocale(language),
    formType: oneOf(
      recommendation.formType,
      META_FORM_TYPES,
      META_INSTANT_FORM_TYPE_MORE_VOLUME
    ),
    introHeadline: truncateText(recommendation.introHeadline, limits.headline),
    introDescription: truncateText(
      recommendation.introDescription,
      limits.body
    ),
    questionsHeadline: truncateText(
      recommendation.questionsHeadline,
      limits.headline
    ),
    contactFields: resolvedContactFields,
    customQuestions,
    privacyPolicyUrl: keepUrl(recommendation.privacyPolicyUrl, limits.url),
    privacyPolicyLinkText: truncateText(
      recommendation.privacyPolicyLinkText,
      limits.privacyLinkText
    ),
    thankYouTitle: truncateText(recommendation.thankYouTitle, limits.headline),
    thankYouBody: truncateText(recommendation.thankYouBody, limits.body),
    thankYouAction: oneOf(
      recommendation.thankYouAction,
      META_THANK_YOU_ACTIONS,
      META_INSTANT_FORM_ACTION_VIEW_WEBSITE
    ),
    thankYouButtonText: truncateText(
      recommendation.thankYouButtonText,
      limits.buttonText
    ),
    thankYouUrl: keepUrl(recommendation.thankYouUrl, limits.url),
  };
};

// No language: ModalCreateLeadForm resolves a supported one from the campaign.
export const buildLinkedInLeadFormInitialValues = ({ recommendation } = {}) => {
  if (!isValidLeadForm(recommendation, LINKEDIN)) return null;

  const limits = LINKEDIN_LEAD_FORM_FIELD_LIMITS;
  const profileFields = uniqueAllowedValues({
    values: recommendation.profileFields,
    allowed: LINKEDIN_PROFILE_FIELD_VALUES,
    max: MAX_LINKEDIN_LEAD_FORM_QUESTIONS,
  });
  const profileBudget =
    profileFields.length > 0
      ? profileFields.length
      : DEFAULT_LINKEDIN_LEAD_FORM_PROFILE_FIELDS.length;
  const initialValues = {
    name: truncateText(recommendation.name, limits.name),
    headline: truncateText(recommendation.headline, limits.headline),
    description: truncateText(recommendation.description, limits.description),
    customQuestions: sanitizeCustomQuestions({
      customQuestions: recommendation.customQuestions,
      maxCount: Math.max(0, MAX_LINKEDIN_LEAD_FORM_QUESTIONS - profileBudget),
    }),
    privacyPolicyUrl: keepUrl(recommendation.privacyPolicyUrl, limits.url),
    legalDisclaimer: truncateText(
      recommendation.legalDisclaimer,
      limits.legalDisclaimer
    ),
    thankYouMessage: truncateText(
      recommendation.thankYouMessage,
      limits.thankYouMessage
    ),
    callToActionLabel: oneOf(
      recommendation.callToActionLabel,
      LINKEDIN_CALL_TO_ACTIONS,
      DEFAULT_LINKEDIN_LEAD_FORM_CALL_TO_ACTION
    ),
    landingPageUrl: keepUrl(recommendation.landingPageUrl, limits.url),
  };

  if (profileFields.length > 0) {
    initialValues.profileFields = profileFields;
  }

  return initialValues;
};

const safePlatform = (platform) =>
  platform === META || platform === LINKEDIN ? platform : null;

const readRecommendationCounts = (recommendations) => {
  let hasLegacy = false;
  let groupsCount = 0;
  try {
    const leadForm = recommendations?.leadForm;
    hasLegacy = Boolean(leadForm) && typeof leadForm === "object";
  } catch {
    hasLegacy = false;
  }
  try {
    const groups = recommendations?.leadFormGroups;
    groupsCount = Array.isArray(groups) ? groups.length : 0;
  } catch {
    groupsCount = 0;
  }
  return { hasLegacy, groupsCount };
};

const reportPrefillFailure = ({
  error,
  platform,
  source,
  recommendations,
  dispatch,
  launchError,
}) => {
  if (typeof dispatch !== "function" || typeof launchError !== "function") {
    return;
  }
  const { hasLegacy, groupsCount } = readRecommendationCounts(recommendations);
  const reportedError = new Error("prepareRecommendedLeadFormCreate failed");
  reportedError.name = SAFE_ERROR_NAMES.has(error?.name) ? error.name : "Error";
  dispatch(
    launchError(reportedError, false, [], {
      functionName: "prepareRecommendedLeadFormCreate",
      context: {
        platform: safePlatform(platform),
        source,
        hasLegacy,
        groupsCount,
      },
      tags: { action: "prefillLeadForm" },
      level: "error",
    })
  );
};

// launchError is LaunchError at the call site. It is injected because this
// module is loaded by node --test, and errors.js pulls constants.js, which
// that runner cannot load. The reported error uses a fixed message so a
// throwing getter cannot carry recommendation text into Sentry.
export const prepareRecommendedLeadFormCreate = ({
  platform,
  recommendations,
  entityID,
  adsGroups,
  language,
  dispatch,
  launchError,
} = {}) => {
  let source = null;
  try {
    const resolved = resolveRecommendedLeadFormForEntity({
      platform,
      recommendations,
      entityID,
      adsGroups,
      usableOnly: true,
    });
    if (!resolved) return EMPTY_PREPARED_LEAD_FORM;
    source = resolved.source;
    const initialValues =
      platform === LINKEDIN
        ? buildLinkedInLeadFormInitialValues({
            recommendation: resolved.recommendation,
          })
        : buildMetaLeadFormInitialValues({
            recommendation: resolved.recommendation,
            language,
          });
    if (!initialValues) return EMPTY_PREPARED_LEAD_FORM;
    return {
      initialValues,
      adIDs: resolved.adIDs,
      source: resolved.source,
    };
  } catch (error) {
    reportPrefillFailure({
      error,
      platform,
      source,
      recommendations,
      dispatch,
      launchError,
    });
    return EMPTY_PREPARED_LEAD_FORM;
  }
};

const isOpenDraft = (entity) => {
  const status = entity?.status;
  return status == null || status === "" || status === OPEN_DRAFT_STATUS;
};

const indexedPath = (collection, key, isList) =>
  isList ? `${collection}.[${key}]` : `${collection}.${key}`;

const destinationTypeForAd = (adsGroups, currentAdID) => {
  const currentKey = entityKeyOf(currentAdID);
  if (!currentKey) return "";
  for (const [, adGroup] of entriesOf(adsGroups)) {
    for (const [, ad] of entriesOf(adGroup?.ads)) {
      if (String(ad?.id || "") === currentKey) {
        return adGroup?.destinationType || "";
      }
    }
  }
  return "";
};

export const collectOpenDraftFormTargets = ({
  platform,
  adsGroups,
  assetsGroups,
  adIDs,
  currentAdID,
} = {}) => {
  const allowed = Array.isArray(adIDs) ? new Set(adIDs.map(String)) : null;
  const destinationType = allowed
    ? ""
    : destinationTypeForAd(adsGroups, currentAdID);
  const targets = [];

  if (platform === META) {
    const groupsAreList = Array.isArray(adsGroups);
    entriesOf(adsGroups).forEach(([groupKey, adGroup]) => {
      if (
        !allowed &&
        destinationType &&
        adGroup?.destinationType !== destinationType
      ) {
        return;
      }
      const adsAreList = Array.isArray(adGroup?.ads);
      entriesOf(adGroup?.ads).forEach(([adKey, ad]) => {
        if (!ad?.id) return;
        if (allowed && !allowed.has(String(ad.id))) return;
        if (!isOpenDraft(ad) || ad.instantForm?.id) return;
        const groupPath = indexedPath("adsGroups", groupKey, groupsAreList);
        const adPath = indexedPath(`${groupPath}.ads`, adKey, adsAreList);
        targets.push(`${adPath}.instantForm`);
      });
    });
    return targets;
  }

  if (platform === LINKEDIN) {
    const groupsAreList = Array.isArray(assetsGroups);
    entriesOf(assetsGroups).forEach(([groupKey, assetsGroup]) => {
      if (!assetsGroup?.id) return;
      if (allowed && !allowed.has(String(assetsGroup.id))) return;
      if (!isOpenDraft(assetsGroup) || assetsGroup.leadForm?.id) return;
      const groupPath = indexedPath("assetsGroups", groupKey, groupsAreList);
      targets.push(`${groupPath}.leadForm`);
    });
  }

  return targets;
};

export const assignRecommendedFormToOpenDrafts = ({
  platform,
  formValue,
  adIDs,
  currentAdID,
  getValues,
  setValue,
}) => {
  if (!formValue?.id || typeof setValue !== "function") return;
  const targets = collectOpenDraftFormTargets({
    platform,
    adsGroups: getValues?.("adsGroups"),
    assetsGroups: getValues?.("assetsGroups"),
    adIDs,
    currentAdID,
  });
  const setOptions = { shouldDirty: true, shouldValidate: true };
  targets.forEach((path) => setValue(path, formValue, setOptions));
};

// E1 save: the ad the user is editing always receives the new form.
// Other open drafts receive it only when they have no form yet.
export const applyCreatedLeadFormFromCard = ({
  platform,
  formValue,
  hasRecommendation,
  adIDs,
  currentAdID,
  getValues,
  setValue,
  onChange,
} = {}) => {
  if (!formValue?.id || typeof onChange !== "function") return;
  onChange(formValue);
  if (!hasRecommendation) return;
  assignRecommendedFormToOpenDrafts({
    platform,
    formValue,
    adIDs,
    currentAdID,
    getValues,
    setValue,
  });
};

// Seleccionar keeps creationTime as stored. Crear falls back to now, matching
// the list modal's create path.
export const toSelectedInstantFormValue = ({
  platform,
  form,
  fallbackCreationTime = false,
}) => {
  if (!form?.id) return null;

  if (platform === LINKEDIN) {
    return {
      id: form.id,
      name: form.name,
      ...pickLeadFormPreviewStorageFields(LINKEDIN, form),
    };
  }

  if (platform === META) {
    return {
      id: form.id,
      name: form.name,
      creationTime: fallbackCreationTime
        ? form.creationTime || Date.now()
        : form.creationTime,
      ...pickLeadFormPreviewStorageFields(META, form),
    };
  }

  return null;
};
